import { Injectable } from '@angular/core';
import { AnetCommonService } from '../anet/auth/anet-common.service';
import { HttpClient } from '@angular/common/http';
import { AthleticUser } from '../anet/models/athletic-user.interface';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DataContext } from '../../my-meet/models/data-context.model';
import { Follow } from '../models/follow.interface';
import { AthleticFollowInfo } from '../anet/models/athletic-follow-info.interface';

@Injectable({
  providedIn: 'root',
})
export class AliveFollowService {
  constructor(
    private common: AnetCommonService,
    private http: HttpClient,
  ) {}

  async getFollows(meetId: number, dataSourceId?: number): Promise<Follow[]> {
    return firstValueFrom(
      this.http.get(`${environment.baseAPIEndpoint}/follows/my?meetId=` + meetId).pipe(
        map((data: any) => {
          if (!data) return [];
          const obj: Follow[] = data;
          if (dataSourceId) {
            return obj.filter((item) => {
              return +item.dataSourceId === dataSourceId;
            });
          }
          return obj;
        }),
      ),
    );
  }

  async addAliveIdsToAnetFollows(
    meetId: number,
    followInfo: AthleticFollowInfo | null,
  ): Promise<AthleticFollowInfo | null> {
    if (followInfo) {
      let findAthletes;
      let findTeams;
      if (followInfo.athletes && followInfo.athletes.length > 0) {
        findAthletes = firstValueFrom(
          this.http.get(
            `${environment.baseAPIEndpoint}/meets/live-athlete-ids?meetId=` +
              meetId +
              '&ids=' +
              followInfo.athletes
                .map((athlete) => {
                  return athlete.AthleteID;
                })
                .join(','),
          ),
        );
      } else {
        findAthletes = Promise.resolve([]);
      }
      if (followInfo.teams && followInfo.teams.length > 0) {
        findTeams = firstValueFrom(
          this.http.get(
            `${environment.baseAPIEndpoint}/meets/live-team-ids?meetId=` +
              meetId +
              '&ids=' +
              followInfo.teams
                .map((team) => {
                  return team.TeamId;
                })
                .join(','),
          ),
        );
      } else {
        findTeams = Promise.resolve([]);
      }

      const liveIds: any[] = await Promise.all([findAthletes, findTeams]);

      if (liveIds[0] && followInfo.athletes) {
        followInfo?.athletes.forEach((athlete, idx) => {
          if (liveIds[0][athlete.AthleteID]) {
            followInfo.athletes[idx].athleticLiveId = liveIds[0][athlete.AthleteID];
          }
        });
      }
      if (liveIds[1] && followInfo.teams) {
        followInfo?.teams.forEach((team, idx) => {
          if (liveIds[1][team.TeamId]) {
            followInfo.teams[idx].athleticLiveId = liveIds[1][team.TeamId];
          }
        });
      }
    }

    // Remove followInfo.teams and .athletes that do not have an athleticNetId
    if (followInfo && followInfo.teams) {
      followInfo.teams = followInfo.teams.filter((team) => {
        return team.athleticLiveId !== undefined;
      });
    }
    if (followInfo && followInfo.athletes) {
      followInfo.athletes = followInfo.athletes.filter((athlete) => {
        return athlete.athleticLiveId !== undefined;
      });
    }

    return followInfo;
  }

  async createFollow(dataContext: DataContext, meetId: number) {
    return firstValueFrom(
      this.http.post(`${environment.baseAPIEndpoint}/follows`, {
        dataContext: dataContext.dataContext,
        dataSourceId: dataContext.athleticLiveId,
        dataSourceType: dataContext.dataSourceType,
        meetId,
      }),
    );
  }

  async deleteFollow(id: string) {
    return firstValueFrom(this.http.delete(`${environment.baseAPIEndpoint}/follows/` + id));
  }
}
